import { TriggerModel, TriggerModes, TriggerMoments } from "@/components/builder/form/traits/HasTriggers";
import { AttachmentsContract, instanceOfAttachmentsEntry } from "../blueprints/attachments";
import StorageService from "@/modules/core/files/services/StorageService";
import { instanceOfUserGroupEntry } from "../blueprints/logito-user-group";
import TriggersService from "@/modules/low-code/services/TriggersService";
import { BlueprintManagerProxy } from "@/components/builder/form/managers/ExpressionManager";
import { getAllComponents } from "@/store/triggers";
import { Blueprint } from "@/components/builder/base/blueprints/Blueprint";
import { Entry } from "@/components/builder/form/entries/Entry";
import { Alerts } from "@/plugins/alerts";
import { isEqual } from 'lodash';
import { computed, inject, unref, type ComputedRef, type Ref } from 'vue';

/** When several identical form instances are shown (BPMN multiform), field emits must use a unique trigger channel per document. */
export function resolveDynamicTriggerChannelId(
    blueprintId: string,
    multiformDocumentGuid: string | undefined | null
): string
{
    return multiformDocumentGuid ? `${blueprintId}:${multiformDocumentGuid}` : blueprintId;
}

/**
 * Use inside field blueprint setup. Parent `FormBlueprint` provides `bpmnMultiformDocumentGuid` when embedded in BPMN multiform modal.
 */
export function useDynamicTriggerChannelId(getBlueprintId: () => string): () => string
{
    const multiformGuidRef = inject<Ref<string | undefined> | ComputedRef<string | undefined> | undefined>(
        'bpmnMultiformDocumentGuid',
        undefined
    );
    const fallback = computed(() => undefined as string | undefined);
    const guidSource = multiformGuidRef ?? fallback;

    return () => resolveDynamicTriggerChannelId(getBlueprintId(), unref(guidSource));
}

export function useDynamicTrigger(
    events: any,
    formId: string
)
{
    const eventName = `dynamic-trigger-${formId}`;

    const eventOn = (
        builder: any,
        baseFormBuilder: any,
        licence: string,
        fullId: string,
        reloadMethod: (result: any) => void,
        alert: Alerts,
        entry: any,
        formObj: any,
        props: any) =>
    {
        events.$on(eventName, (moment: TriggerMoments, blueprint: any, tableRowIndex: number | null) =>
        {
            runTrigger(moment, blueprint, tableRowIndex, builder, baseFormBuilder, licence, fullId, reloadMethod, alert, entry, formObj, props, events);
        });
    };

    const eventOff = () =>
    {
        events.$off(eventName);
    };

    const emit = (moment: TriggerMoments, blueprint: any, tableRowIndex?: number | null) =>
    {
        events.$emit(eventName, moment, blueprint, tableRowIndex);
    };

    return {
        eventOn,
        eventOff,
        emit
    };
}

export function triggerExists(blueprint: any)
{
    if ('serverSideTriggers' in blueprint && blueprint.serverSideTriggers && blueprint.serverSideTriggers.length > 0)
        return true;

    if ('clientSideTriggers' in blueprint && blueprint.clientSideTriggers && blueprint.clientSideTriggers.length > 0)
        return true;

    return false;
}

export async function runTrigger(
    moment: TriggerMoments,
    blueprint: any,
    tableRowIndex: number | null,
    builder: any,
    baseFormBuilder: any,
    licence: string,
    fullId: string,
    reloadMethod: (result: any, blueprint: any, tableRowIndex: number | null) => void,
    alert: Alerts,
    entry: any,
    formObj: any,
    props: any,
    events: any)
{
    const executeDynamicTrigger = async (mode: TriggerModes, trigger: any) =>
    {
        const executeServerSideTrigger = async () =>
        {
            const additonalData: Record<string, any> = {};
            const data = (await builder.collectEntry(async (blueprint: Blueprint, entry: Entry) =>
            {
                instanceOfAttachmentsEntry(entry) &&
                        (await entry.upload(
                            blueprint as AttachmentsContract,
                            async (file) => await StorageService.upload(file, 'documents')
                        ));
                instanceOfUserGroupEntry(entry) && (additonalData[`${blueprint.name}_type`] = entry.settings?.key);
            })) as Record<string, any>;

            const baseData = (await baseFormBuilder.collectEntry(async (blueprint: Blueprint, entry: Entry) =>
            {
                instanceOfAttachmentsEntry(entry) &&
                            (await entry.upload(
                                blueprint as AttachmentsContract,
                                async (file) => await StorageService.upload(file, 'documents')
                            ));
                instanceOfUserGroupEntry(entry) && (additonalData[`${blueprint.name}_type`] = entry.settings?.key);
            })) as Record<string, any>;

            if (trigger.script)
            {
                const trimmedFullId = fullId.startsWith('/') ? fullId.slice(1) : fullId;

                const result = await TriggersService.ExecuteScript(licence, trimmedFullId, trigger.script.key, trigger.scriptConfigId, null, tableRowIndex, data, baseData);

                if (result)
                    serverSideResults.push(result);

                reloadMethod(result, blueprint, tableRowIndex);
            }
        };

        const executeClientSideTrigger = async () =>
        {
            if (trigger.scriptVal === null || trigger.scriptVal === '' || trigger.scriptVal === undefined)
                return;


            const scriptCode = trigger.scriptVal;

            const fn = new Function('Entry', 'Blueprint', 'FormDOM', 'Builder', 'Form', 'Props', 'Components', 'TableRowIndex', 'Values', 'SrvResults', 'Events', 'TriggerMoments', `
              return (async () => {
                ${scriptCode}
              })();
            `);

            const form = new BlueprintManagerProxy(builder.schema);
            const components = getAllComponents(builder.blueprint.id);
            const values = trigger.scriptConfig ? JSON.parse(trigger.scriptConfig) : {};

            await fn.call(null, entry, blueprint, formObj.value, builder, form, props, components, tableRowIndex, values, serverSideResults, events, TriggerMoments);

            reloadMethod(true, blueprint, tableRowIndex);
        };

        try
        {
            if (mode === TriggerModes.ServerSide)
                await executeServerSideTrigger();

            if (mode === TriggerModes.ClientSide)
                await (executeClientSideTrigger());
        }
        catch (ex: any)
        {
            alert.warning(ex.message);
        }
    };

    const serverSideResults: any[] = [];

    if ('serverSideTriggers' in blueprint && blueprint.serverSideTriggers)
    {
        const triggers = blueprint.serverSideTriggers.filter((trigger: TriggerModel) => trigger.moment.key === moment);

        for (const trigger of triggers)
        {
            await executeDynamicTrigger(TriggerModes.ServerSide, trigger);
        }
    }

    if ('clientSideTriggers' in blueprint && blueprint.clientSideTriggers)
    {
        const triggers = blueprint.clientSideTriggers.filter((trigger: TriggerModel) => trigger.moment.key === moment);

        for (const trigger of triggers)
            await executeDynamicTrigger(TriggerModes.ClientSide, trigger);
    }

    return {
        executeDynamicTrigger
    };
}

export function emitBlueprintChanges(
    $events: any,
    formId: any,
    blueprint: any,
    currentBlueprint: any,
    entry: any,
    oldEntry: any,
    tableRowIndex: number | null,
)
{
    const componentTriggers = (component: any) =>
    {
        newDataArray.forEach((_, index) =>
        {
            try
            {
                let oldValue = oldDataArray[index]?.[component?.name]?.data;
                let newValue = newDataArray[index]?.[component?.name]?.data;

                if (Array.isArray(oldValue) && oldValue.length === 0 || oldValue === undefined)
                    oldValue = null;

                if (Array.isArray(newValue) && newValue.length === 0 || newValue === undefined)
                    newValue = null;

                if (!isEqual(oldValue, newValue) && blueprint?.name !== component?.name)
                {
                    useDynamicTrigger($events, formId).emit(TriggerMoments.onChange, component, tableRowIndex);
                }
            }
            catch (ex: any)
            {
                console.error(`emitBlueprintChanges: component - ${component?.name ?? ''} - error: ${ex?.message ?? ''}`, ex);
            }
        });
    };

    if (blueprint && blueprint.name !== currentBlueprint.name)
    {
        useDynamicTrigger($events, formId).emit(TriggerMoments.onChange, currentBlueprint, null);
    }

    if (!('components' in currentBlueprint) || tableRowIndex == null) return;

    const components = currentBlueprint.components as any[];
    const newEntry = JSON.parse(JSON.stringify(entry));
    const newDataArray = newEntry.data as any[];
    const oldDataArray = oldEntry.data;

    components.forEach(component =>
    {
        componentTriggers(component);
    });
}
